import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Switch } from "@arcadeai/design-system/components/ui/atoms/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { useNavigate } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import {
  useDeleteMcpGateway,
  usePatchMcpGateway,
} from "@/app/mcp-gateways/api/hooks";
import { useGetUserSourcesQuery } from "@/app/user-sources/api/hooks";
import { DeleteDialog } from "@/components/custom/dialog/delete-dialog";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import type { McpServer } from "../../types";
import { ConnectDrawer } from "../connect/connect-drawer";

export const McpGatewayActions = ({ gateway }: { gateway: McpServer }) => {
  const isStatic = gateway?.binding?.type === "static";
  const isEnabled = gateway?.status === "active";
  const navigate = useNavigate();
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const { data: userSourcesData } = useGetUserSourcesQuery();
  const { mutate: patchGateway, isPending } = usePatchMcpGateway(
    gateway.id,
    gateway.binding.type
  );
  const { mutate: deleteGateway, isPending: isDeleting } = useDeleteMcpGateway(
    gateway.binding.type
  );

  // Only flag the source as disabled once the user-sources query has resolved.
  // Without this guard, `userSourcesData` is undefined on first paint, the
  // .find() returns undefined, and `?.status !== "active"` is true — flashing
  // the switch into a misleading disabled state with the "linked User Source
  // is disabled" tooltip before data arrives.
  const isUserSourceDisabled =
    userSourcesData != null &&
    gateway.auth_type === "user_source" &&
    gateway.user_source_id != null &&
    userSourcesData.data.items.find((s) => s.id === gateway.user_source_id)
      ?.status !== "active";

  let toggleTooltip = isEnabled ? "Disable MCP gateway" : "Enable MCP gateway";
  if (isUserSourceDisabled) {
    toggleTooltip = "Cannot enable — linked User Source is disabled";
  }

  if (isStatic) {
    return (
      <div className="flex items-center justify-start">
        <span className="text-muted-foreground text-sm">—</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-start gap-3">
      <CanProject permission={PROJECT.mcpGateways.update}>
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger
              render={
                <span
                  aria-busy={isPending}
                  className="mr-1 inline-flex items-center"
                />
              }
            >
              <Switch
                checked={isEnabled}
                disabled={isUserSourceDisabled}
                loading={isPending}
                onCheckedChange={(checked) =>
                  patchGateway({ status: checked ? "active" : "inactive" })
                }
              />
            </TooltipTrigger>
            <TooltipContent>
              <p>{toggleTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CanProject>
      <ConnectDrawer gateway={gateway} />
      <CanProject permission={PROJECT.mcpGateways.update}>
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  aria-label="Edit MCP Gateway"
                  onClick={() =>
                    navigate({
                      params: {
                        gatewayId: gateway.id,
                        organizationId,
                        projectId,
                      },
                      search: { binding: gateway.binding.type },
                      to: "/orgs/$organizationId/projects/$projectId/mcp-gateways/$gatewayId/edit",
                    })
                  }
                  size="icon"
                  variant="ghost"
                />
              }
            >
              <Pencil className="size-4" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Edit MCP Gateway</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CanProject>
      <CanProject permission={PROJECT.mcpGateways.delete}>
        <DeleteDialog
          description="Are you sure you want to delete this MCP gateway? This action cannot be undone."
          isLoading={isDeleting}
          onDelete={() => deleteGateway(gateway.id)}
          title="Delete MCP Gateway"
          tooltipText="Delete MCP gateway"
        />
      </CanProject>
    </div>
  );
};
